import { useState, useEffect } from 'react';
import { Building2, Mail, Phone, MapPin, Save } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';

interface CompanyFormData {
  id: string;
  name: string;
  address?: string;
  contact?: string;       // Contact person name
  phone?: string;
  email?: string;
  description?: string;
}

interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (company: CompanyFormData) => void;
  company?: CompanyFormData;
  mode: 'add' | 'edit';
}

const CompanyModal = ({ isOpen, onClose, onSave, company, mode }: CompanyModalProps) => {
  const toast = useToast();
  const [formData, setFormData] = useState<CompanyFormData>(
    company || {
      id: '',
      name: '',
      address: '',
      contact: '',
      phone: '',
      email: '',
      description: '',
    }
  );

  useEffect(() => {
    if (company) {
      setFormData(company);
    }
  }, [company]);

  const handleChange = (field: keyof CompanyFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.name || formData.name.trim().length === 0) {
      toast.error('Validation Error', 'Company name is required');
      return;
    }

    // Validate email format if provided
    if (formData.email && formData.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Validation Error', 'Please enter a valid email address');
        return;
      }
    }

    // Validate phone format if provided (basic validation)
    if (formData.phone && formData.phone.trim().length > 0) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(formData.phone)) {
        toast.error('Validation Error', 'Please enter a valid phone number');
        return;
      }
    }

    onSave(formData);
    onClose();
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Company' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add New Company' : `Edit Company: ${company?.name}`}
      size="2xl"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-primary-600" />
            <span>Basic Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Acme Corporation"
                className="input-base w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person
              </label>
              <input
                type="text"
                value={formData.contact || ''}
                onChange={(e) => handleChange('contact', e.target.value)}
                placeholder="e.g., John Doe"
                className="input-base w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>Phone</span>
              </label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="e.g., +1 (555) 123-4567"
                className="input-base w-full"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>Email</span>
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="e.g., contact@acme.com"
                className="input-base w-full"
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <MapPin className="w-5 h-5 text-primary-600" />
            <span>Additional Details</span>
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="e.g., 123 Main St, New York, NY 10001"
                className="input-base w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description or notes about this company..."
                className="input-base w-full"
                rows={3}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CompanyModal;
